import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';

const STORAGE_KEY = 'saved_customer_code';

export const useCustomerCode = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [customerId, setCustomerId] = useState('');
  const [saveCustomerCode, setSaveCustomerCode] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const typingTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 로컬스토리지에서 저장된 고객사 코드 불러오기 (초기 마운트 시에만)
  useEffect(() => {
    if (!isInitialized) {
      const savedCustomerCode = localStorage.getItem(STORAGE_KEY);
      if (savedCustomerCode) {
        setCustomerId(savedCustomerCode);
        setSaveCustomerCode(true);
      }
      setIsInitialized(true);
    }
  }, [isInitialized]);

  // URL에서 customer_code 또는 alias 추출 (초기 마운트 시에만, 사용자가 입력 중이 아닐 때만)
  useEffect(() => {
    if (!isInitialized || isUserTyping) return;
    
    const customerCodeParam = searchParams.get('customer_code');
    const aliasParam = searchParams.get('alias');
    
    if (customerCodeParam || aliasParam) {
      const code = customerCodeParam || aliasParam || '';
      // 현재 값과 다를 때만 업데이트
      if (code !== customerId) {
        setCustomerId(code);
        setSaveCustomerCode(false);
      }
      return;
    }

    // 서브도메인에서 고객사 코드 추출 (초기 로드 시에만)
    if (!customerId) {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      if (parts.length >= 2) {
        const subdomain = parts[0];
        const domain = parts.slice(1).join('.');
        
        if (domain === 'localhost' || domain.includes('gensapps.com')) {
          if (subdomain && subdomain !== 'www' && subdomain !== 'console') {
            setCustomerId(subdomain);
            setSaveCustomerCode(false);
          }
        }
      }
    }
  }, [searchParams, location, isInitialized, isUserTyping, customerId]);

  const updateCustomerId = useCallback((newCustomerId: string) => {
    setIsUserTyping(true);
    setCustomerId(newCustomerId);
    // 이전 타이머가 있으면 취소
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }
    // 입력이 끝난 후 일정 시간이 지나면 자동 감지 재활성화
    typingTimerRef.current = setTimeout(() => {
      setIsUserTyping(false);
      typingTimerRef.current = null;
    }, 2000); // 2초 후 자동 감지 재활성화
  }, []);

  const updateSaveCustomerCode = useCallback((checked: boolean) => {
    setSaveCustomerCode(checked);
    if (checked && customerId.trim()) {
      localStorage.setItem(STORAGE_KEY, customerId.trim());
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [customerId]);

  const saveCustomerCodeToStorage = useCallback(() => {
    if (saveCustomerCode && customerId.trim()) {
      localStorage.setItem(STORAGE_KEY, customerId.trim());
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [saveCustomerCode, customerId]);

  return {
    customerId,
    saveCustomerCode,
    updateCustomerId,
    updateSaveCustomerCode,
    saveCustomerCodeToStorage,
  };
};
